import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import PlanForm from "@/components/PlanForm";
import {
  Plus, Edit3, Eye, EyeOff, Trash2, Loader2, DollarSign,
  Building2, Users, CheckCircle, XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function PlanManager() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("list");
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/tenant/admin/plans`, { headers: getHeaders() });
      setPlans(data.plans || []);
    } catch (err) {
      console.error("Failed to fetch plans:", err);
      toast.error("Failed to load subscription plans");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (planData) => {
    try {
      setSaving(true);
      await axios.post(`${API}/tenant/admin/plans`, planData, { headers: getHeaders() });
      toast.success("Plan created successfully");
      setActiveTab("list");
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create plan");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (planData) => {
    if (!editingPlan) return;
    try {
      setSaving(true);
      await axios.put(`${API}/tenant/admin/plans/${editingPlan.id}`, planData, { headers: getHeaders() });
      toast.success("Plan updated successfully");
      setEditingPlan(null);
      setActiveTab("list");
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update plan");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (planId) => {
    if (!window.confirm("Deactivate this plan? It will no longer be available for new subscriptions.")) return;
    try {
      await axios.delete(`${API}/tenant/admin/plans/${planId}`, { headers: getHeaders() });
      toast.success("Plan deactivated");
      fetchPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to deactivate plan");
    }
  };

  const formatPrice = (cents) => {
    if (!cents || cents === 0) return "Free";
    return `R${(cents / 100).toFixed(2)}`;
  };

  const getPlanTypeIcon = (type) => {
    return type === "CORPORATE" ? <Building2 className="w-4 h-4" /> : <Users className="w-4 h-4" />;
  };

  return (
    <div>
      <TopNav />
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Subscription Plans</h1>
            <p className="text-muted-foreground">Manage pricing plans available for registration</p>
          </div>
          <Button onClick={() => { setEditingPlan(null); setActiveTab("create"); }}>
            <Plus className="w-4 h-4 mr-2" /> New Plan
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="list">All Plans</TabsTrigger>
            <TabsTrigger value="create">{editingPlan ? "Edit Plan" : "Create Plan"}</TabsTrigger>
          </TabsList>

          <TabsContent value="list" className="mt-4">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>
            ) : plans.length === 0 ? (
              <Card className="p-8 text-center text-muted-foreground">
                No subscription plans yet. Create your first plan.
              </Card>
            ) : (
              <div className="grid gap-4">
                {plans.map((plan) => {
                  const hubCount = Object.values(plan.hubs || {}).filter(Boolean).length;
                  return (
                    <Card key={plan.id} className={`p-4 ${!plan.is_active ? "opacity-60" : ""}`}>
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-semibold">{plan.name}</span>
                            <Badge variant={plan.plan_type === "CORPORATE" ? "default" : "secondary"}>
                              {plan.plan_type}
                            </Badge>
                            <Badge variant={plan.is_active ? "outline" : "destructive"}>
                              {plan.is_active ? "Active" : "Inactive"}
                            </Badge>
                            {plan.is_public ? (
                              <Eye className="w-4 h-4 text-muted-foreground" />
                            ) : (
                              <EyeOff className="w-4 h-4 text-muted-foreground" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{plan.description}</p>
                          <div className="flex items-center gap-4 text-sm mt-1">
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              {formatPrice(plan.price_monthly)}/mo
                            </span>
                            {plan.price_yearly > 0 && (
                              <span>{formatPrice(plan.price_yearly)}/yr</span>
                            )}
                            {plan.price_per_user_monthly > 0 && (
                              <span>{formatPrice(plan.price_per_user_monthly)}/user/mo</span>
                            )}
                            <span>{plan.billing_period === "none" ? "Free" : plan.billing_period}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline">{hubCount} hubs</Badge>
                            {plan.features?.max_team_members && (
                              <Badge variant="outline">Up to {plan.features.max_team_members} users</Badge>
                            )}
                            {plan.features?.max_storage_gb && (
                              <Badge variant="outline">{plan.features.max_storage_gb}GB</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingPlan(plan);
                              setActiveTab("create");
                            }}
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          {plan.is_active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeactivate(plan.id)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="create" className="mt-4">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">
                {editingPlan ? `Edit: ${editingPlan.name}` : "Create New Plan"}
              </h2>
              <PlanForm
                initialData={editingPlan}
                onSave={editingPlan ? handleUpdate : handleCreate}
                onCancel={() => { setEditingPlan(null); setActiveTab("list"); }}
                saving={saving}
              />
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
